import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { App, Spin, type TableProps } from "antd";
import { useMemo, useState, type ComponentType, type SVGProps } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CloneIcon from "sipli-fleet-core/assets/icons/clone-icon.svg";
import DeleteIcon from "sipli-fleet-core/assets/icons/delete-icon.svg";
import {
  CustomButton,
  CustomTable,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedConfirmationModal from "sipli-fleet-core/components/CustomSharedConfirmationModal/CustomSharedConfirmationModal";
import { ARROW_DOWN_ICON, BACK_ICON, MAPPING_RIGHT_ARROW_ICON } from "src/assets/svgs";
import {
  useMutationDeleteMappingPreset,
  useQueryGetMappingPresetById,
  useQueryGetMappingPresetProperties,
  useQueryGetMappingPresets,
} from "src/queries/mappingPreset.queries";
import type {
  MappingPresetPropertyRecord,
  MappingPresetRecord,
} from "src/services/mappingPresetService";
import { cn } from "../../../../../../sipli-fleet-core/utils/cn";
import { useQueryGetVehicleById } from "../../../../../queries/unitManagement.queries";
import CustomerBreadcrumb from "../../CustomerBreadcrumb";
import { getMappingsByPacketCode, safeString } from "./mapping.helpers";
import type { MappingCardContext, MappingRecord } from "./mapping.types";
import MappingAddEdit from "./MappingAddEdit";
import MappingCardHeader from "./MappingCardHeader";
import { AllMappingsDrawer, MappingDetailsDrawer } from "./MappingDetails";
import {
  getPresetName,
  getPresetPropertyLastValue,
  getPresetRecords,
  getPresetTotal,
  getUniquePresetProperties,
  toPresetIncomingValues,
} from "./mappingPreset.helpers";
import { CreatePresetModal } from "./MappingPresets";

const DeleteIconComponent = DeleteIcon as unknown as ComponentType<SVGProps<SVGSVGElement>>;
const CloneIconComponent = CloneIcon as unknown as ComponentType<SVGProps<SVGSVGElement>>;
const BackIcon = BACK_ICON as unknown as ComponentType<SVGProps<SVGSVGElement>>;

const DEFAULT_PAGINATION = {
  page: 0,
  size: 10,
};

const getPresetTypeLabel = (
  preset: MappingPresetRecord,
  t: (key: string, options?: Record<string, string>) => string
) =>
  preset.isBase
    ? t("base_presets", { defaultValue: "Base Presets" })
    : t("created_preset", { defaultValue: "Created preset" });

type DetailRecord = Record<string, unknown>;

const asRecord = (value: unknown): DetailRecord =>
  value && typeof value === "object" ? (value as DetailRecord) : {};

const readFirstValue = (records: DetailRecord[], keys: string[]): unknown => {
  for (const record of records) {
    for (const key of keys) {
      const value = record[key];

      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
  }

  return undefined;
};

const displayValue = (value: unknown) => {
  if (value === undefined || value === null || value === "") return "--";
  if (typeof value === "boolean") return value ? "ON" : "OFF";
  if (typeof value === "string" || typeof value === "number") return value;

  const record = asRecord(value);
  return String(readFirstValue([record], ["name", "label", "title", "value", "code"]) ?? "--");
};

const displayText = (value: unknown) => String(displayValue(value));

const PageHeader = ({
  title,
  currentLabel,
  presetPath,
  showPresetParent = false,
}: {
  title: string;
  currentLabel: string;
  presetPath?: string;
  showPresetParent?: boolean;
}) => {
  const { t } = useTranslation();
  const { clientId, unitId, vehicleId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const stateDevice = (state as { device?: { vehicle?: unknown } } | null)?.device;

  const { data: vehicleDetail } = useQueryGetVehicleById(vehicleId, {
    enabled: !!vehicleId,
  });

  const stateVehicleName = displayText(stateDevice?.vehicle);
  const apiVehicleName = displayText(
    readFirstValue([asRecord(vehicleDetail)], ["vehicleName", "name", "label", "model"])
  );

  const breadcrumbVehicleName =
    stateVehicleName !== "--"
      ? stateVehicleName
      : apiVehicleName !== "--"
        ? apiVehicleName
        : `${t("vehicle")} ${vehicleId ?? ""}`.trim();

  const vehiclePath = `/customers/${encodeURIComponent(clientId ?? "")}/${encodeURIComponent(
    unitId ?? ""
  )}/${encodeURIComponent(vehicleId ?? "")}?tab=info`;

  const presetsPath =
    presetPath ??
    `/customers/${encodeURIComponent(clientId ?? "")}/${encodeURIComponent(
      unitId ?? ""
    )}/${encodeURIComponent(vehicleId ?? "")}/presets`;

  const backTarget = showPresetParent ? presetsPath : vehiclePath;

  return (
    <div className="mb-7">
      <div className="customer-detail-header">
        <div className="flex items-center gap-3">
          <BackIcon
            className="w-[16px] cursor-pointer"
            onClick={() => navigate(backTarget, { state })}
            aria-label="Back to presets"
          />
          <CustomTypography.CustomTitle
            level={3}
            className="customer-detail-title !text-[25.23px] !m-0 !font-bold"
          >
            {title}
          </CustomTypography.CustomTitle>
        </div>
        <div className="min-w-0 col-start-2">
          <CustomerBreadcrumb
            ariaLabel={title}
            separator="/"
            items={[
              {
                label: t("customers"),
                path: "/customers",
              },
              {
                label: t("vehicle_list"),
                path: `/customers/${encodeURIComponent(clientId ?? "")}?tab=devices`,
                state,
              },
              {
                label: breadcrumbVehicleName,
                path: vehiclePath,
                state,
              },
              ...(showPresetParent
                ? [
                    {
                      label: t("preset", { defaultValue: "Preset" }),
                      path: presetsPath,
                      state,
                    },
                  ]
                : []),
              {
                label: currentLabel,
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export const MappingPresetListPage = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { clientId, unitId, vehicleId } = useParams();
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [cloneRecord, setCloneRecord] = useState<MappingPresetRecord | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<MappingPresetRecord | null>(null);
  const [sortConfig, setSortConfig] = useState({
    sort: "DESC",
    sortBy: "isBase",
  });
  const presetsQuery = useQueryGetMappingPresets({
    page: pagination.page,
    size: pagination.size,
    sort: sortConfig.sort,
    sortBy: sortConfig.sortBy,
  });
  const deleteMutation = useMutationDeleteMappingPreset();
  const records = useMemo(() => getPresetRecords(presetsQuery.data), [presetsQuery.data]);
  const totalCount = getPresetTotal(presetsQuery.data, records.length);

  const handleTableChange: TableProps<MappingPresetRecord>["onChange"] = (
    _pagination,
    _filters,
    sorter
  ) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (!currentSorter?.order) {
      setSortConfig({
        sort: "DESC",
        sortBy: "isBase",
      });
      setPagination((prev) => ({ ...prev, page: 0 }));
      return;
    }

    const columnKey = String(currentSorter.columnKey ?? currentSorter.field ?? "");

    const sortByMap: Record<string, string> = {
      setName: "setName",
      presetType: "isBase",
      deviceMake: "make",
      deviceModel: "model",
    };

    setSortConfig({
      sort: currentSorter.order === "ascend" ? "ASC" : "DESC",
      sortBy: sortByMap[columnKey] ?? "isBase",
    });

    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleDelete = async () => {
    if (!deleteRecord) return;

    try {
      await deleteMutation.mutateAsync(deleteRecord.id);
      message.success(
        t("preset_deleted_successfully", { defaultValue: "Preset Deleted Successfully!" })
      );
      setDeleteRecord(null);
    } catch {
      message.error(t("error"));
    }
  };

  const columns: TableProps<MappingPresetRecord>["columns"] = [
    {
      title: t("name"),
      dataIndex: "setName",
      key: "setName",
      sorter: true,
      render: (_value, record) => (
        <CustomTypography.CustomText className="!text-sm !font-[600] !leading-[18px] !text-black">
          {getPresetName(record)}
        </CustomTypography.CustomText>
      ),
    },
    {
      title: t("preset_type", { defaultValue: "Preset type" }),
      key: "presetType",
      sorter: true,
      render: (_, record) => (
        <CustomTag
          className={cn(
            "",
            record.isBase
              ? "bg-green4 text-green2"
              : "bg-gray1 text-gray7"
          )}
        >
          {getPresetTypeLabel(record, t)}
        </CustomTag>
      ),
    },
    {
      title: t("device_preset", { defaultValue: "Device preset" }),
      key: "deviceMake",
      // sorter: true,
      render: (_, record) => (
        <CustomTypography.CustomText className="!font-[600] !leading-[18px]">
          {record.deviceMake?.name ?? "--"}
        </CustomTypography.CustomText>
      ),
    },
    {
      title: t("deviceModel", { defaultValue: "Device Model" }),
      key: "deviceModel",
      // sorter: true,
      render: (_, record) => (
        <CustomTypography.CustomText className="!leading-[18px]">
          {record.deviceModel?.name ?? "--"}
        </CustomTypography.CustomText>
      ),
    },
    {
      title: t("actions"),
      key: "actions",
      align: "center",
      width: 150,
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          <CustomTooltip title={t("clone", { defaultValue: "Clone" })}>
            <CloneIconComponent
              onClick={(event) => {
                event?.stopPropagation?.();
                setCloneRecord(record);
              }}
            />
          </CustomTooltip>

          <CustomTooltip
            title={
              record.isBase
                ? t("base_presets_cannot_be_deleted", {
                    defaultValue: "Base presets cannot be deleted.",
                  })
                : t("delete")
            }
          >
            <DeleteIconComponent
              onClick={(event) => {
                event?.stopPropagation?.();
                if (!record.isBase) setDeleteRecord(record);
              }}
              disabled={record.isBase}
              className={`w-[15px] h-[16px] ${record.isBase ? "opacity-30 grayscale !cursor-not-allowed" : ""}`}
            />
          </CustomTooltip>
        </div>
      ),
    },
  ];

  return (
    <section className="min-h-full bg-gray1 px-6 py-7">
      <PageHeader
        title={t("preset", { defaultValue: "Preset" })}
        currentLabel={t("preset", { defaultValue: "Preset" })}
      />

      <div className="overflow-hidden rounded-[16px] bg-white">
        <CustomTable<MappingPresetRecord>
          data={records}
          columns={columns}
          rowKey={(record) => String(record.id)}
          isLoading={presetsQuery.isLoading || presetsQuery.isFetching}
          pageSize={pagination.size}
          currentPage={pagination.page}
          totalCount={totalCount}
          handlePagination={(page) => setPagination((prev) => ({ ...prev, page }))}
          handlePageSizeChange={(size) => setPagination({ page: 0, size: Number(size) })}
          onChange={handleTableChange}
          showSearchInput={false}
          showCustomizeColumns={false}
          showDownload={false}
          isCustomRowClick
          customRowClick={(record) =>
            navigate(`/customers/${clientId}/${unitId}/${vehicleId}/presets/${record.id}`)
          }
          locale={{ emptyText: t("no_data") }}
        />
      </div>

      <CreatePresetModal
        open={Boolean(cloneRecord)}
        mode="clone"
        modelId={cloneRecord?.deviceModel?.id}
        cloneRecord={cloneRecord}
        onClose={() => setCloneRecord(null)}
      />
      <CustomSharedConfirmationModal
        open={Boolean(deleteRecord)}
        title={t("delete_preset", { defaultValue: "Delete preset" })}
        description={t("delete_preset_confirmation", {
          defaultValue: "Are you sure you want to delete this preset?",
        })}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        isLoading={deleteMutation.isPending}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDelete}
        centered
      />
    </section>
  );
};

const PresetMappingCard = ({
  title,
  mappingCount,
  readOnly,
  readOnlyTooltip,
  onAddMapping,
  onOpenMappings,
}: {
  title: string;
  mappingCount: number;
  readOnly?: boolean;
  readOnlyTooltip?: string;
  onAddMapping: () => void;
  onOpenMappings: () => void;
}) => {
  const { t, i18n } = useTranslation();

  return (
    <div
      role="button"
      tabIndex={0}
      className="flex min-h-[87px] w-[331px] flex-col justify-between rounded-[16px] border-2 border-primary bg-gray17 p-[16px]"
    >
      <div className="text-[15.75px] font-[600] leading-[20px] text-black">{title}</div>

      <div className="flex items-center justify-between gap-3">
        <CustomTooltip title={readOnly ? readOnlyTooltip : ""}>
          <span>
            <button
              type="button"
              disabled={readOnly}
              onClick={(event) => {
                event.stopPropagation();
                if (!readOnly) onAddMapping();
              }}
              className={cn(
                "text-[14px] font-[600] leading-[18px] underline",
                readOnly
                  ? "cursor-not-allowed text-gray6 opacity-60"
                  : "cursor-pointer text-primary"
              )}
            >
              {t("addMapping")}
            </button>
          </span>
        </CustomTooltip>

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenMappings();
          }}
          className="flex min-w-0 cursor-pointer items-center gap-[6px] disabled:cursor-text"
        >
          <CustomTag
            interactive={mappingCount > 0}
            className={cn("bg-transparent text-gray8", {
              "bg-primary text-white flex":
                mappingCount > 0,
            })}
          >
            {formatNumber(mappingCount, i18n.language)}
          </CustomTag>
          <span
            className={cn("text-[14px] text-gray8 font-medium", {
              "font-[600] leading-[18px] text-primary underline": mappingCount > 0,
            })}
          >
            {/* {t("mapping", { defaultValue: "mapping" })} */}
            mapping
          </span>
          {mappingCount > 0 && <img src={MAPPING_RIGHT_ARROW_ICON} alt="" className="" />}
        </button>
      </div>
    </div>
  );
};

export const MappingPresetDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { clientId, unitId, vehicleId, presetId } = useParams();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    presetFields: true,
  });
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [cardContext, setCardContext] = useState<MappingCardContext | null>(null);
  const [detailsContext, setDetailsContext] = useState<MappingCardContext | null>(null);
  const presetListQuery = useQueryGetMappingPresets({
    page: 0,
    size: 1000,
    sort: "ASC",
    sortBy: "isBase",
  });
  const [isAllMappingsOpen, setIsAllMappingsOpen] = useState(false);
  const detailQuery = useQueryGetMappingPresetById(presetId, { enabled: Boolean(presetId) });
  const propertyQuery = useQueryGetMappingPresetProperties(presetId, {
    enabled: Boolean(presetId),
  });
  const presets = useMemo(() => getPresetRecords(presetListQuery.data), [presetListQuery.data]);
  const preset = presets.find((item) => String(item.id) === String(presetId));
  const mappings = useMemo<MappingRecord[]>(
    () => detailQuery.data?.data.response ?? [],
    [detailQuery.data?.data.response]
  );
  const properties = useMemo<MappingPresetPropertyRecord[]>(
    () => getUniquePresetProperties(propertyQuery.data?.data.response ?? [], mappings),
    [mappings, propertyQuery.data?.data.response]
  );
  const mappedProperties = useMemo<MappingPresetPropertyRecord[]>(() => {
    return properties.filter((property) => {
      const packetCode = String(property.packetCode ?? "");
      return getMappingsByPacketCode(mappings, packetCode).length > 0;
    });
  }, [mappings, properties]);
  const groupName = preset ? getPresetName(preset) : t("title", { defaultValue: "Title" });
  const incomingValues = useMemo(
    () => toPresetIncomingValues(mappedProperties, groupName),
    [groupName, mappedProperties]
  );
  const isBasePreset = preset?.isBase === true;
  const basePresetReadOnlyTooltip = t("base_preset_read_only", {
    defaultValue: "Base presets are read only. You cannot add or edit mappings.",
  });

  const modelId = preset?.deviceModel?.id;
  const backPath = `/customers/${clientId}/${unitId}/${vehicleId}/presets`;
  const isLoading = presetListQuery.isLoading || detailQuery.isLoading || propertyQuery.isLoading;
  const firstProperty = mappedProperties[0];
  const firstContext = firstProperty
    ? {
        parentKey: groupName,
        childKey: firstProperty.propertyName || String(firstProperty.packetCode ?? ""),
        packetCode: String(firstProperty.packetCode ?? ""),
        lastValue: getPresetPropertyLastValue(firstProperty),
        mappingData: getMappingsByPacketCode(mappings, String(firstProperty.packetCode ?? "")),
      }
    : null;

  const displayableMappingsCount = useMemo(() => {
    const ids = new Set<string>();

    mappedProperties.forEach((property) => {
      const packetCode = String(property.packetCode ?? "");

      getMappingsByPacketCode(mappings, packetCode).forEach((mapping) => {
        ids.add(String(mapping.id));
      });
    });

    return ids.size;
  }, [mappedProperties, mappings]);

  return (
    <section className="min-h-full bg-gray1 px-6 py-7">
      <PageHeader
        title={t("preset_details", { defaultValue: "Preset details" })}
        currentLabel={
          preset ? getPresetName(preset) : t("preset_details", { defaultValue: "Preset details" })
        }
        presetPath={backPath}
        showPresetParent
      />

      <div className="rounded-[16px] border border-gray3 bg-white p-4">
        <CustomTypography.CustomText className="!mb-8 !block !text-[19.93px] !font-bold !leading-[28px] !text-black">
          {t("data_monitoring_and_configuration")}
        </CustomTypography.CustomText>

        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            if (displayableMappingsCount) setIsAllMappingsOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            if (displayableMappingsCount) setIsAllMappingsOpen(true);
          }}
          className={cn(
            "mb-4 flex min-h-[64px] w-full items-center justify-between rounded-[8px] border border-primary bg-gray17 px-4 text-left hover:bg-primary6",
            {
              "cursor-pointer": displayableMappingsCount > 0,
              "cursor-default": displayableMappingsCount === 0,
            }
          )}
        >
          <span className="flex items-center gap-[10px]">
            <span className="text-[17.72px] font-bold leading-[28px] text-primary">
              {t("mappings")}
            </span>
            <span className="flex h-[22px] py-0 min-w-[38px] items-center justify-center rounded-[24px] bg-primary px-[10px] text-[14px] leading-[18px] font-[600] font-semibold leading-none text-white">
              {formatNumber(displayableMappingsCount, i18n.language)}
            </span>
          </span>

          <span className="flex items-center gap-4">
            <CustomTooltip title={isBasePreset ? basePresetReadOnlyTooltip : ""}>
              <span>
                <CustomButton
                  label={t("addMapping", { defaultValue: "Add mapping" })}
                  type="primary"
                  disabled={isBasePreset || !firstContext}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!isBasePreset && firstContext) {
                      setCardContext(null);
                      setIsMappingModalOpen(true);
                    }
                  }}
                  className={cn(
                    "rounded-[4px] px-6 h-[32px] !text-[14px] !font-semibold !leading-[18px] !space-x-[0.05px] !text-gray2",
                    {
                      "!cursor-not-allowed !opacity-60": isBasePreset,
                    }
                  )}
                />
              </span>
            </CustomTooltip>

            <img src={ARROW_DOWN_ICON} alt="" className="-rotate-90" />
          </span>
        </div>

        {isLoading ? (
          <div className="flex min-h-[240px] items-center justify-center">
            <Spin />
          </div>
        ) : !mappedProperties.length ? (
          <div className="py-10 text-center text-gray9">{t("no_data")}</div>
        ) : (
          <div>
            <MappingCardHeader
              title={safeString(groupName)}
              count={mappedProperties.length}
              open={Boolean(openGroups.presetFields)}
              onToggle={() =>
                setOpenGroups((prev) => ({
                  ...prev,
                  presetFields: !prev.presetFields,
                }))
              }
            />

            {openGroups.presetFields && (
              <div className="flex flex-wrap gap-4 pt-6">
                {mappedProperties.map((property) => {
                  const propertyName = property.propertyName || String(property.packetCode ?? "");
                  const packetCode = String(property.packetCode ?? "");
                  const mappingData = getMappingsByPacketCode(mappings, packetCode);
                  const context: MappingCardContext = {
                    parentKey: groupName,
                    childKey: propertyName,
                    packetCode,
                    lastValue: getPresetPropertyLastValue(property),
                    mappingData,
                  };

                  return (
                    <PresetMappingCard
                      key={`${packetCode}-${propertyName}`}
                      title={safeString(propertyName)}
                      mappingCount={mappingData.length}
                      readOnly={isBasePreset}
                      readOnlyTooltip={basePresetReadOnlyTooltip}
                      onAddMapping={() => {
                        setCardContext(context);
                        setIsMappingModalOpen(true);
                      }}
                      onOpenMappings={() => setDetailsContext(context)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <AllMappingsDrawer
        open={isAllMappingsOpen}
        onClose={() => setIsAllMappingsOpen(false)}
        unitId={unitId}
        incomingValues={incomingValues}
        mappings={mappings}
        mode="preset"
        presetId={presetId}
        deviceModelId={modelId}
        drawerWidth={393}
        readOnly={isBasePreset}
      />

      <MappingDetailsDrawer
        open={Boolean(detailsContext)}
        onClose={() => setDetailsContext(null)}
        unitId={unitId}
        incomingValues={incomingValues}
        mappings={detailsContext?.mappingData ?? []}
        availabilityMappings={mappings}
        childKey={detailsContext?.childKey}
        mode="preset"
        presetId={presetId}
        deviceModelId={modelId}
        readOnly={isBasePreset}
        drawerWidth={393}
      />
      {unitId && (
        <MappingAddEdit
          open={isMappingModalOpen}
          unitId={unitId}
          incomingValues={incomingValues}
          mappings={mappings}
          context={cardContext}
          mode="preset"
          presetId={presetId}
          deviceModelId={modelId}
          isBasePreset={isBasePreset}
          onClose={() => {
            setIsMappingModalOpen(false);
            setCardContext(null);
          }}
        />
      )}
    </section>
  );
};
